// Build a sensible day-by-day plan for a given stop, picking activities
// and restaurants from the destination's full catalog and slotting them
// into Day 1 (arrival, light), middle days (anchor + support + dinner),
// and the final day (relaxed morning + brunch).
//
// Pure — never mutates user picks. The UI lets the user "apply" a plan,
// which then calls toggleActivity / toggleRestaurant for each suggestion.

const ANCHOR_CATEGORIES = new Set(['culture', 'iconic', 'tour', 'excursion', 'adventure']);
const SUPPORT_CATEGORIES = new Set(['food_drink', 'culture', 'beaches', 'tour', 'transport']);

// Score an activity for use as the day's "anchor" — biased toward higher
// rating, iconic vibes, and full/half-day duration.
function anchorScore(a) {
  let s = 0;
  if (a.rating) s += a.rating * 10;
  if (a.vibe_tags?.includes('iconic')) s += 8;
  if (a.vibe_tags?.includes('culture')) s += 4;
  if (a.vibe_tags?.includes('romantic')) s += 3;
  if (a.vibe_tags?.includes('adventure')) s += 4;
  if (/full day|all day|excursion/i.test(a.duration || '')) s += 6;
  if (/half day/i.test(a.duration || '')) s += 4;
  if (ANCHOR_CATEGORIES.has(a.category)) s += 5;
  return s;
}

// Score for "support" / secondary activity — lighter and shorter.
function supportScore(a) {
  let s = 0;
  if (a.rating) s += a.rating * 8;
  if (/1-2 hours|2 hours|90 min|1 hour/i.test(a.duration || '')) s += 6;
  if (SUPPORT_CATEGORIES.has(a.category)) s += 4;
  if (a.vibe_tags?.includes('food')) s += 5;
  if (a.vibe_tags?.includes('romantic')) s += 3;
  // De-prioritize iconic + full-day for support (those should be anchors)
  if (a.vibe_tags?.includes('iconic')) s -= 4;
  if (/full day/i.test(a.duration || '')) s -= 6;
  return s;
}

// Pick the best N items by score, removing them from the pool.
function takeBest(pool, scoreFn, n) {
  if (n <= 0 || pool.length === 0) return [];
  const sorted = [...pool].sort((a, b) => scoreFn(b) - scoreFn(a));
  const picked = sorted.slice(0, n);
  return picked;
}

// Build a day-by-day plan for ONE stop.
//   nights — how many nights at this stop
//   activities — available activities for this stop (already filtered by sub-city)
//   restaurants — available restaurants
//   isFirstStop — affects Day 1 framing (arrival vs transition)
//   isLastStop — affects last day (departure morning vs transit to next)
export function buildDailyPlan({ destination, nights, activities = [], restaurants = [], isFirstStop = true, isLastStop = true }) {
  if (!destination || nights < 1) return [];

  // Working pools — we drain them as we assign so nothing repeats
  const actPool = activities.filter(a => a.category !== 'transport'); // skip "Lisbon → Porto Express Train"-type
  const transportActs = activities.filter(a => a.category === 'transport');
  const fineDining = restaurants.filter(r => r.category === 'fine_dining' || r.price_range === '$$$$');
  const localFavs = restaurants.filter(r => r.category === 'local_favorite' || r.price_range === '$$$');
  const casual = restaurants.filter(r => r.category === 'casual' || r.price_range === '$$');

  // Total days to plan = nights + 1 (arrival day + middle days + departure day)
  // For multi-stop trips, the final day at a stop is the transit day, not
  // a relaxed brunch day.
  const totalDays = nights + (isLastStop ? 1 : 0); // last stop adds departure day

  const days = [];

  // Decide how many fine-dining slots to use (1-2 max per stop)
  const fineDiningSlotsToUse = Math.min(fineDining.length, nights >= 3 ? 2 : 1);
  const fineDiningPlannedDays = new Set();
  // Schedule them on the 2nd day and a mid-stay day if there's room
  if (fineDiningSlotsToUse >= 1) fineDiningPlannedDays.add(Math.min(1, totalDays - 1));
  if (fineDiningSlotsToUse >= 2) fineDiningPlannedDays.add(Math.min(Math.floor(totalDays / 2) + 1, totalDays - 2));

  // Index pointers — pick the best available each time
  for (let i = 0; i < totalDays; i++) {
    const isArrival = i === 0;
    const isDeparture = isLastStop && i === totalDays - 1;
    const isTransitOut = !isLastStop && i === totalDays - 1; // last day at this stop before moving to next stop

    const dayPlan = {
      dayIndex: i,
      label: isArrival ? 'Arrival day'
        : isDeparture ? 'Departure day'
        : isTransitOut ? `Transit day (heading to next stop)`
        : `Full day ${i}`,
      morning: null,
      afternoon: null,
      evening: null,
      meals: { lunch: null, dinner: null },
      notes: [],
    };

    if (isArrival) {
      // Light arrival: 1 short activity, casual dinner
      const light = takeBest(
        actPool.filter(a => /1 hour|90 min|2 hours|short/i.test(a.duration || '') || a.vibe_tags?.includes('romantic')),
        supportScore,
        1
      );
      if (light[0]) {
        dayPlan.afternoon = light[0];
        actPool.splice(actPool.indexOf(light[0]), 1);
      }
      const dinner = takeBest(localFavs.length > 0 ? localFavs : casual, r => r.rating * 10 + (r.vibe_tags?.includes('local_favorite') ? 5 : 0), 1);
      if (dinner[0]) {
        dayPlan.meals.dinner = dinner[0];
        const idx = restaurants.indexOf(dinner[0]);
        if (idx >= 0) restaurants.splice(idx, 1);
        if (localFavs.includes(dinner[0])) localFavs.splice(localFavs.indexOf(dinner[0]), 1);
        if (casual.includes(dinner[0])) casual.splice(casual.indexOf(dinner[0]), 1);
      }
      dayPlan.notes.push('Keep it light — settle in, get oriented, find your rhythm. Save the big sights for tomorrow when you\'re fresh.');
    } else if (isDeparture) {
      // Departure morning: 1 short activity near hotel + brunch
      const morn = takeBest(
        actPool.filter(a => /1 hour|2 hours|short/i.test(a.duration || '')),
        supportScore,
        1
      );
      if (morn[0]) {
        dayPlan.morning = morn[0];
        actPool.splice(actPool.indexOf(morn[0]), 1);
      }
      const brunch = takeBest(casual.length > 0 ? casual : localFavs, r => r.rating * 10, 1);
      if (brunch[0]) {
        dayPlan.meals.lunch = brunch[0];
        const idx = restaurants.indexOf(brunch[0]);
        if (idx >= 0) restaurants.splice(idx, 1);
      }
      dayPlan.notes.push('Departure day — last brunch, one final walk through the city, then head to the airport.');
    } else if (isTransitOut) {
      // Last day at this stop before moving to next stop. Light morning,
      // then transit. Include any "transport" activities (Lisbon→Porto
      // Express Train, etc.) here.
      const morn = takeBest(
        actPool.filter(a => /1 hour|2 hours|short/i.test(a.duration || '')),
        supportScore,
        1
      );
      if (morn[0]) {
        dayPlan.morning = morn[0];
        actPool.splice(actPool.indexOf(morn[0]), 1);
      }
      if (transportActs.length > 0) {
        dayPlan.afternoon = transportActs[0];
        dayPlan.notes.push(`After lunch, travel to the next city — ${transportActs[0].description || ''}`);
      } else {
        dayPlan.notes.push('After lunch, head to the next city. Check the route ribbon for train/flight options.');
      }
      const lunch = takeBest(casual.length > 0 ? casual : localFavs, r => r.rating * 10, 1);
      if (lunch[0]) {
        dayPlan.meals.lunch = lunch[0];
        const idx = restaurants.indexOf(lunch[0]);
        if (idx >= 0) restaurants.splice(idx, 1);
      }
    } else {
      // Full day: anchor + supporting activity + dinner
      const anchor = takeBest(actPool, anchorScore, 1);
      if (anchor[0]) {
        dayPlan.morning = anchor[0];
        actPool.splice(actPool.indexOf(anchor[0]), 1);
      }
      const support = takeBest(actPool, supportScore, 1);
      if (support[0]) {
        dayPlan.afternoon = support[0];
        actPool.splice(actPool.indexOf(support[0]), 1);
      }
      // Dinner: fine dining on scheduled days, else local favorite
      if (fineDiningPlannedDays.has(i) && fineDining.length > 0) {
        const fd = takeBest(fineDining, r => (r.rating || 0) * 10, 1);
        if (fd[0]) {
          dayPlan.meals.dinner = fd[0];
          fineDining.splice(fineDining.indexOf(fd[0]), 1);
          const idx = restaurants.indexOf(fd[0]);
          if (idx >= 0) restaurants.splice(idx, 1);
        }
      } else {
        const dinner = takeBest(
          localFavs.length > 0 ? localFavs : (casual.length > 0 ? casual : fineDining),
          r => (r.rating || 0) * 10,
          1
        );
        if (dinner[0]) {
          dayPlan.meals.dinner = dinner[0];
          const idx = restaurants.indexOf(dinner[0]);
          if (idx >= 0) restaurants.splice(idx, 1);
          if (localFavs.includes(dinner[0])) localFavs.splice(localFavs.indexOf(dinner[0]), 1);
        }
      }
    }

    days.push(dayPlan);
  }

  return days;
}

// Collect every activity / restaurant id referenced in a plan, so the
// "Apply this plan" button can toggle them all into the user's picks.
export function collectPlanPicks(planDays) {
  const activityIds = new Set();
  const restaurantIds = new Set();
  for (const d of planDays) {
    if (d.morning?.id) activityIds.add(d.morning.id);
    if (d.afternoon?.id) activityIds.add(d.afternoon.id);
    if (d.evening?.id) activityIds.add(d.evening.id);
    if (d.meals?.lunch?.id) restaurantIds.add(d.meals.lunch.id);
    if (d.meals?.dinner?.id) restaurantIds.add(d.meals.dinner.id);
  }
  return { activityIds: [...activityIds], restaurantIds: [...restaurantIds] };
}
